import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBorderAll,
  faRectangleLandscape as rectangleLandscapeDuotone,
  faVectorSquare,
} from '@fortawesome/pro-duotone-svg-icons';
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faArrowAltDown,
  faArrowAltLeft,
  faArrowAltRight,
  faArrowAltUp,
  faBadge,
  faBell,
  faBolt,
  faBookmark,
  faBorderStyle,
  faCircle,
  faCloud,
  faCoffee,
  faComment,
  faComments,
  faCopy,
  faDatabase,
  faDiamond,
  faDotCircle,
  faEdit,
  faEllipsisVertical,
  faExclamationTriangle,
  faFile,
  faFileDownload,
  faFolder,
  faHandPointRight,
  faHeart,
  faHexagon,
  faImage,
  faKey,
  faLaughBeam,
  faLockAlt,
  faLongArrowAltRight,
  faMagnifyingGlass,
  faObjectGroup,
  faObjectUngroup,
  faOctagon,
  faPaperPlane,
  faPen,
  faRectangleLandscape,
  faSave,
  faSquare,
  faSquareFull,
  faStar,
  faStickyNote,
  faTint,
  faTriangle,
  faUnlockAlt,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBold,
  faBringForward,
  faChevronDown,
  faChevronUp,
  faCircle as solidCircle,
  faEllipsisH,
  faFont,
  faGripHorizontal,
  faLocationArrow,
  faMobileAlt,
  faSendBackward,
} from '@fortawesome/pro-solid-svg-icons';
import { AclPermissionModule } from '@y42/acl/shared/permission';
import { ImageCropModule } from '@y42/image-manipulation/crop';
import { IntercomModule } from '@y42/intercom';
import { ColorPickerModule } from 'ngx-color-picker';
import { CanvasContextMenuToolbarComponent } from './canvas-context-menu-toolbar/canvas-context-menu-toolbar.component';
import { EmojiDirective } from './canvas-context-menu-toolbar/emoji/emoji.directive';
import { EmojiHelperModule } from './canvas-context-menu-toolbar/emoji/emoji.module';
import { ContextMenuAlignNodesComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-align-nodes/context-menu-align-nodes.component';
import { ContextMenuAlignmentComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-alignment/context-menu-alignment.component';
import { ContextMenuBorderColorComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-border-color/context-menu-border-color.component';
import { ContextMenuBorderStyleComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-border-style/context-menu-border-style.component';
import { ContextMenuBringToComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-bring-to/context-menu-bring-to.component';
import { ContextMenuColorButtonComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-color-button/context-menu-color-button.component';
import { ContextMenuColorPipe } from './canvas-context-menu-toolbar/toolbar-items/context-menu-color-button/context-menu-color.pipe';
import { ContextMenuDeleteComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-delete/context-menu-delete.component';
import { ContextMenuDuplicateComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-duplicate/context-menu-duplicate.component';
import { ContextMenuEditComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-edit/context-menu-edit.component';
import { ContextMenuFontFamilyComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-font-family/context-menu-font-family.component';
import { ContextMenuFontSizeComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-font-size/context-menu-font-size.component';
import { ContextMenuFontWeightComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-font-weight/context-menu-font-weight.component';
import { ContextMenuGroupComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-group/context-menu-group.component';
import { ContextMenuLockComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-lock/context-menu-lock.component';
import { ContextMenuMoveToTabComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-move-to-tab/context-menu-move-to-tab.component';
import { ContextMenuRefreshComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-refresh/context-menu-refresh.component';
import { ContextMenuShadowComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-shadow/context-menu-shadow.component';
import { ContextMenuShapeColorComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-shape-color/context-menu-shape-color.component';
import { ContextMenuTextColorComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-text-color/context-menu-text-color.component';
import { ContextMenuTextHighlightComponent } from './canvas-context-menu-toolbar/toolbar-items/context-menu-text-highlight/context-menu-text-highlight.component';
import { CanvasMainToolbarComponent } from './canvas-main-toolbar/canvas-main-toolbar.component';
import { CreateCommentButtonComponent } from './canvas-main-toolbar/toolbar-items/create-comment-button/create-comment-button.component';
import { CreateDrawingButtonComponent } from './canvas-main-toolbar/toolbar-items/create-drawing-button/create-drawing-button.component';
import { DownloadPngComponent } from './canvas-main-toolbar/toolbar-items/download-png-button/download-png-button.component';
import { GroupNodesButtonComponent } from './canvas-main-toolbar/toolbar-items/group-nodes-button/group-nodes-button.component';
import { InsertAttentionNodeButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-attention-node-button/insert-attention-node-button.component';
import { InsertConnectionLineButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-connection-line-button/insert-connection-line-button.component';
import { InsertEmojiButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-emoji-button/insert-emoji-button.component';
import { InsertFilterButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-filter-button/insert-filter-button.component';
import { InsertFrameButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-frame-button/insert-frame-button.component';
import { InsertShapeButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-shape-button/insert-shape-button.component';
import { InsertStickyNoteButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-sticky-note-button/insert-sticky-note-button.component';
import { InsertTextareaButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-textarea-button/insert-textarea-button.component';
import { InsertWidgetButtonComponent } from './canvas-main-toolbar/toolbar-items/insert-widget-button/insert-widget-button.component';
import { ShowGridButtonComponent } from './canvas-main-toolbar/toolbar-items/show-grid-button/show-grid-button.component';
import { ShowMoreMenuButtonComponent } from './canvas-main-toolbar/toolbar-items/show-more-menu-button/show-more-menu-button.component';
import { ToggleSelectionButtonComponent } from './canvas-main-toolbar/toolbar-items/toggle-selection-button/toggle-selection-button.component';
import { UploadImageButtonComponent } from './canvas-main-toolbar/toolbar-items/upload-image-button/upload-image-button.component';

const components = [
  // container components
  CanvasMainToolbarComponent,
  CanvasContextMenuToolbarComponent,

  // internal toolbar components
  ContextMenuAlignmentComponent,
  ContextMenuAlignNodesComponent,
  ContextMenuBorderStyleComponent,
  ContextMenuBringToComponent,
  ContextMenuColorButtonComponent,
  ContextMenuDeleteComponent,
  ContextMenuDuplicateComponent,
  ContextMenuEditComponent,
  ContextMenuFontFamilyComponent,
  ContextMenuFontSizeComponent,
  ContextMenuGroupComponent,
  ContextMenuLockComponent,
  ContextMenuMoveToTabComponent,
  ContextMenuRefreshComponent,
  ContextMenuShadowComponent,
  ContextMenuBorderColorComponent,
  ContextMenuShapeColorComponent,
  ContextMenuTextColorComponent,
  ContextMenuTextHighlightComponent,
  ContextMenuFontWeightComponent,

  InsertAttentionNodeButtonComponent,
  CreateCommentButtonComponent,
  DownloadPngComponent,
  CreateDrawingButtonComponent,
  InsertEmojiButtonComponent,
  InsertFilterButtonComponent,
  InsertFrameButtonComponent,
  UploadImageButtonComponent,
  GroupNodesButtonComponent,
  InsertTextareaButtonComponent,
  InsertConnectionLineButtonComponent,
  InsertShapeButtonComponent,
  InsertStickyNoteButtonComponent,
  InsertWidgetButtonComponent,
  ToggleSelectionButtonComponent,
  ShowMoreMenuButtonComponent,
  ShowGridButtonComponent,
];

// TODO: Split this module into two modules, one for the toolbar and one for the context menu.
@NgModule({
  declarations: [
    ...components,

    // pipes
    ContextMenuColorPipe,
  ],
  imports: [
    ColorPickerModule,
    CommonModule,
    DragDropModule,
    EmojiHelperModule,
    FontAwesomeModule,
    ImageCropModule,
    IntercomModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatSliderModule,
    MatTooltipModule,
    OverlayModule,
    AclPermissionModule,
  ],
  exports: [...components, EmojiDirective],
})
export class CanvasComponentsModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(
      faAlignCenter,
      faAlignLeft,
      faAlignRight,
      faArrowAltDown,
      faArrowAltLeft,
      faArrowAltRight,
      faArrowAltUp,
      faBadge,
      faBell,
      faBold,
      faBolt,
      faBookmark,
      faBorderAll,
      faBorderStyle,
      faBringForward,
      faChevronDown,
      faChevronUp,
      faCircle,
      faCloud,
      faCoffee,
      faComment,
      faComments,
      faCopy,
      faDatabase,
      faDiamond,
      faDotCircle,
      faEdit,
      faEllipsisH,
      faEllipsisVertical,
      faExclamationTriangle,
      faFile,
      faFileDownload,
      faFolder,
      faFont,
      faGripHorizontal,
      faHandPointRight,
      faHeart,
      faHexagon,
      faImage,
      faKey,
      faLaughBeam,
      faLocationArrow,
      faLockAlt,
      faLongArrowAltRight,
      faMagnifyingGlass,
      faMobileAlt,
      faObjectGroup,
      faObjectUngroup,
      faOctagon,
      faPaperPlane,
      faPen,
      faRectangleLandscape,
      faSave,
      faSendBackward,
      faSquare,
      faSquareFull,
      faStar,
      faStickyNote,
      faTint,
      faTriangle,
      faUnlockAlt,
      faVectorSquare,
      rectangleLandscapeDuotone,
      solidCircle,
    );
  }
}
